/*
 * The wire-frame envelope for the worker host protocol (ADR 0016).
 *
 * This module sits on the worker kernel's pre-seed surface: its import graph must be exactly
 * zod — no application modules — so the kernel can decode the `init` frame before any
 * config-reading module evaluates. Frame *payloads* stay undecoded here; the host applies the
 * contract's payload codec and the state-key schemas after the deferred setup graph loads.
 */
import { z } from 'zod';

export interface RuntimeEnvPayload {
    readonly APP_CONFIG_NAME: string;
    readonly APP_COMMIT: string;
    readonly APP_VERSION?: string;
}

declare global {
    /**
     * The runtime environment triple: injected into the page by app-ws at serve time, seeded
     * into worker scopes by the kernel from `init.env`. The sole runtime authority for
     * environment resolution (ADR 0016).
     */
    // eslint-disable-next-line no-var -- global scope declarations require var
    var __APP_ENV__: RuntimeEnvPayload | undefined;
}

export const runtimeEnvPayloadSchema = z.object({
    APP_CONFIG_NAME: z.string(),
    APP_COMMIT: z.string(),
    APP_VERSION: z.string().optional(),
}) satisfies z.ZodType<RuntimeEnvPayload>;

export interface SerializedWorkerError {
    readonly name: string;
    readonly message: string;
    readonly code?: string;
    readonly stack?: string;
    readonly cause?: SerializedWorkerError;
}

export const serializedWorkerErrorSchema: z.ZodType<SerializedWorkerError> = z.lazy(() =>
    z.object({
        name: z.string(),
        message: z.string(),
        code: z.string().optional(),
        stack: z.string().optional(),
        cause: serializedWorkerErrorSchema.optional(),
    }),
);

const MAX_CAUSE_DEPTH = 8;

/**
 * Serialize an arbitrary thrown value into the envelope's error shape, walking the `cause`
 * chain. Lives here (not in errors.ts) because the kernel must construct `boot-error` frames
 * without importing application code.
 */
export const serializeUnknownError = (error: unknown, depth = 0): SerializedWorkerError => {
    if (error instanceof Error) {
        const code = 'code' in error && typeof error.code === 'string' ? error.code : undefined;
        return {
            name: error.name,
            message: error.message,
            code,
            stack: error.stack,
            cause:
                error.cause !== undefined && depth < MAX_CAUSE_DEPTH
                    ? serializeUnknownError(error.cause, depth + 1)
                    : undefined,
        };
    }
    return { name: 'Error', message: String(error) };
};

export interface WorkerClientMetadata {
    /** Datadog browser session id of the owning tab, when available. */
    readonly sessionId?: string;
    /** Effective (masquerade-aware) user id as seen by the owning tab. */
    readonly userId?: string;
}

const workerClientMetadataSchema = z.object({
    sessionId: z.string().optional(),
    userId: z.string().optional(),
}) satisfies z.ZodType<WorkerClientMetadata>;

export interface WorkerInitFrame {
    readonly kind: 'init';
    readonly clientId: string;
    readonly client: WorkerClientMetadata;
    readonly env: RuntimeEnvPayload;
    /** Encoded snapshots per composed state key; decoded by the host via each key's schema. */
    readonly state: Readonly<Record<string, unknown>>;
}

export interface WorkerPushFrame {
    readonly kind: 'push';
    readonly key: string;
    readonly payload: unknown;
}

export interface WorkerReadyFrame {
    readonly kind: 'ready';
}

export interface WorkerBootErrorFrame {
    readonly kind: 'boot-error';
    readonly error: SerializedWorkerError;
}

export interface WorkerRequestFrame {
    readonly kind: 'request';
    readonly id: number;
    readonly type: string;
    readonly payload: unknown;
}

export interface WorkerResponseOkFrame {
    readonly kind: 'response';
    readonly id: number;
    readonly ok: true;
    readonly payload: unknown;
}

export interface WorkerResponseErrorFrame {
    readonly kind: 'response';
    readonly id: number;
    readonly ok: false;
    readonly error: SerializedWorkerError;
}

export interface WorkerEventFrame {
    readonly kind: 'event';
    readonly type: string;
    readonly payload: unknown;
}

export interface WorkerPingFrame {
    readonly kind: 'ping';
    readonly seq: number;
}

export interface WorkerPongFrame {
    readonly kind: 'pong';
    readonly seq: number;
}

export type WorkerFrame =
    | WorkerInitFrame
    | WorkerPushFrame
    | WorkerReadyFrame
    | WorkerBootErrorFrame
    | WorkerRequestFrame
    | WorkerResponseOkFrame
    | WorkerResponseErrorFrame
    | WorkerEventFrame
    | WorkerPingFrame
    | WorkerPongFrame;

// PERFORMANCE: the union options are ordered by runtime frequency, hottest first, and that
// order must be preserved. z.union trial-parses options in declaration order and each failed
// trial is expensive (~30x measured on the event path when the hot kinds sat behind the
// boot-time kinds), so the per-tick kinds (event, response, request, push, ping/pong) come
// first and the once-per-boot kinds (init, ready, boot-error) last. A discriminatedUnion would
// be O(1) but zod rejects the duplicate 'response' discriminator.
export const workerFrameSchema = z.union([
    z.object({ kind: z.literal('event'), type: z.string(), payload: z.unknown() }),
    z.object({ kind: z.literal('response'), id: z.number(), ok: z.literal(true), payload: z.unknown() }),
    z.object({ kind: z.literal('response'), id: z.number(), ok: z.literal(false), error: serializedWorkerErrorSchema }),
    z.object({ kind: z.literal('request'), id: z.number(), type: z.string(), payload: z.unknown() }),
    z.object({ kind: z.literal('push'), key: z.string(), payload: z.unknown() }),
    z.object({ kind: z.literal('ping'), seq: z.number() }),
    z.object({ kind: z.literal('pong'), seq: z.number() }),
    z.object({
        kind: z.literal('init'),
        clientId: z.string(),
        client: workerClientMetadataSchema,
        env: runtimeEnvPayloadSchema,
        state: z.record(z.string(), z.unknown()),
    }),
    z.object({ kind: z.literal('ready') }),
    z.object({ kind: z.literal('boot-error'), error: serializedWorkerErrorSchema }),
]) satisfies z.ZodType<WorkerFrame>;

/** Decode an inbound structured-clone datum into a frame; throws ZodError on protocol violation. */
export const parseWorkerFrame = (data: unknown): WorkerFrame => workerFrameSchema.parse(data);

export const safeParseWorkerFrame = (data: unknown): WorkerFrame | undefined => {
    const result = workerFrameSchema.safeParse(data);
    return result.success ? result.data : undefined;
};
